/**
 * Lightweight game-session tracking for Hub score verification.
 *
 * The Hub accepts this object as advisory fraud-detection metadata. It does
 * not block score writes, but missing it makes ranking anomalies harder to
 * inspect after release.
 */

export interface SessionVerification {
  sessionId: string
  seed: number
  durationMs: number
  actionCount: number
  actionsPerSecond: number
  finalScore: number
}

interface ActiveSession {
  sessionId: string
  seed: number
  startedAt: number
}

class GameSessionManager {
  private active: ActiveSession | null = null
  private lastVerification: SessionVerification | null = null

  start(seed: number): string {
    const sessionId = createSessionId()
    this.active = {
      sessionId,
      seed,
      startedAt: now(),
    }
    this.lastVerification = null
    return sessionId
  }

  end(finalScore: number, actionCountOverride?: number): SessionVerification | null {
    if (!this.active) return null

    const durationMs = Math.max(0, Math.round(now() - this.active.startedAt))
    const durationSec = durationMs / 1000
    const actionCount =
      typeof actionCountOverride === 'number'
        ? Math.max(0, Math.round(actionCountOverride))
        : 0
    const verification: SessionVerification = {
      sessionId: this.active.sessionId,
      seed: this.active.seed,
      durationMs,
      actionCount,
      actionsPerSecond:
        durationSec > 0
          ? Number((actionCount / durationSec).toFixed(2))
          : 0,
      finalScore: Math.max(0, Math.round(finalScore)),
    }

    this.lastVerification = verification
    this.active = null
    return verification
  }

  getLastVerification(): SessionVerification | null {
    return this.lastVerification
  }
}

function now(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now()
  }
  return Date.now()
}

function createSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const rand = Math.random().toString(36).slice(2, 12)
  return `session_${Date.now().toString(36)}_${rand}`
}

export function createGameSeed(): number {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const values = new Uint32Array(1)
    crypto.getRandomValues(values)
    return values[0] & 0x7fffffff
  }
  return Math.floor(Math.random() * 0x7fffffff)
}

export const gameSessionManager = new GameSessionManager()
